import React, { useEffect, useMemo, useState } from 'react'
import { Box, Button, IconButton, Popover, Typography, useTheme } from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import moment from 'moment'
import { useAtomValue, useSetAtom } from 'jotai'
import * as sx from '../../common/stylesInline/report_daily_branch'
import { Item } from '../../common/snippets'
import Datepicker from '../../common/datePicker'
import { ReportHeading } from './common/ReportHeading'
import { GetRequest } from '../../common/api'
import { urls, xlCol, xlColq } from '../../common/constants'
import useToken from '../../helpers/useTokens'
import { companySettingsAtom } from '../../common/jotaiState/commonStateAtoms'
import { alertAtom, globalLoaderAtom } from '../../common/jotaiState/globalStateAtoms'
import DropDownOA from '../../common/dropDownOA'
import { TableComponent } from '../../common/TableComponent'
import { useSearchParams } from 'react-router-dom'

const reportName = 'Whatsapp Communication Sent Detail Report'

const messageTypeOptions = [
  { id: 'UTILITY', name: 'Utility' },
  { id: 'AUTHENTICATION', name: 'Authentication' },
  { id: 'MARKETING', name: 'Marketing' },
  { id: 'SERVICE', name: 'Service' },
  { id: 'BUSINESS', name: 'Business Initiated' },
  { id: 'CUSTOMER', name: 'Customer Initiated' },
]
const policyCategoryOptions = [
  //{ id: 'Bus', name: 'Bus' },
  { id: 'Cargo', name: 'Cargo' },
  //{ id: 'MS Web & App', name: 'MS Web & App' },
  //{ id: 'Bus Hire', name: 'Bus Hire' },
  //{ id: 'Boarding Feedback & Loyalty', name: 'Boarding Feedback & Loyalty' },
]
const sentStatusOptions = [
  { id: 1, name: 'Success' },
  { id: 0, name: 'Failed' },
]
const billingStatusOptions = [
  { id: 1, name: 'Billed' },
  { id: 0, name: 'Not Billed' },
]

const criteriaHeaders = [
  { header: 'Sent From Date', field: 'FromDate', xlCol: { dataType: 'string' } },
  { header: 'Sent To Date', field: 'ToDate', xlCol: { dataType: 'string' } },
  { header: 'Message Type', field: 'MessageType', xlCol: { dataType: 'string' } },
  { header: 'Policy Type', field: 'PolicyType', xlCol: { dataType: 'string' } },
  { header: 'Policy Category', field: 'PolicyCategory', xlCol: { dataType: 'string' } },
  { header: 'Sent Status', field: 'SentStatus', xlCol: { dataType: 'string' } },
  { header: 'Billing Status', field: 'BillingStatus', xlCol: { dataType: 'string' } },
]

const WhatsAppDetailedHeaders = [
  { header: 'SN', exportHead: 'SN', field: 'SN', xlCol: xlCol, weight: 8, align: 'right' },

  {
    header: 'Customer Mobile No',
    exportHead: 'Customer Mobile No',
    field: 'MobileNO',
    xlCol: xlCol,
    weight: 18,
  },

  {
    header: 'Message Sent On',
    exportHead: 'Message Sent On',
    field: 'SentDateTime',
    xlCol: xlCol,
    weight: 24,
  },

  {
    header: 'Session Started On',
    exportHead: 'Session Started On',
    field: 'SessionStartTime',
    xlCol: xlCol,
    weight: 18,
  },

  {
    header: 'Whatsapp Message Category',
    exportHead: 'Whatsapp Message Category',
    field: 'Category',
    xlCol: xlCol,
    weight: 18,
  },

  {
    header: 'Policy Type Message Detail',
    exportHead: 'Policy Type Message Detail',
    field: 'Header',
    xlCol: xlCol,
    weight: 34,
  },
  {
    header: 'View Msg',
    exportHead: 'View Msg',
    field: 'ViewMsg_view',
    xlCol: xlCol,
    weight: 14,
    ViewOnly: true,
  },

  {
    header: 'Policy Category',
    exportHead: 'Policy Category',
    field: 'Master',
    xlCol: xlCol,
    weight: 18,
  },

  {
    header: 'Sent Status',
    exportHead: 'Sent Status',
    field: 'ISSent',
    xlCol: xlColq,
    weight: 16,
  },

  {
    header: 'Billing Status',
    exportHead: 'Billing Status',
    field: 'IsBilled',
    xlCol: xlColq,
    weight: 16,
  },

  {
    header: 'Rate w/GST',
    exportHead: 'Rate w/GST',
    field: 'RateAppliedWithGST',
    xlCol: xlColq,
    align: 'right',
    weight: 14,
  },

  {
    header: 'Remarks, if any',
    exportHead: 'Remarks, if any',
    field: 'Remarks',
    xlCol: xlCol,
    weight: 25,
  },
]

export const WhatsAppMessageSentDetails = () => {
  const { loginData } = useToken()
  const [searchParams, setSearchParams] = useSearchParams()
  const { intCompanyID } = loginData || {}
  const companySettings = useAtomValue(companySettingsAtom)
  const { WhatsappTypeID } = companySettings || {}
  const setAlert = useSetAtom(alertAtom)
  const setLoader = useSetAtom(globalLoaderAtom)
  const theme = useTheme()
  const [fromDate, setFromDate] = useState(moment(new Date()))
  const [toDate, setToDate] = useState(moment(new Date()))
  const [messageType, setMessageType] = useState(messageTypeOptions[0])
  const [policyType, setPolicyType] = useState([])
  const [policyCategory, setPolicyCategory] = useState(policyCategoryOptions[0])
  const [sentStatus, setSentStatus] = useState(sentStatusOptions[0])
  const [billingStatus, setBillingStatus] = useState(billingStatusOptions[0])
  const [policyTypeList, setPolicyTypeList] = useState([])
  const [rawTableData, setRawTableData] = useState([])
  const [previewPosition, setPreviewPosition] = useState(null)
  const [previewData, setPreviewData] = useState({
    Header: '',
    MavenMessage: '',
  })

  const criteriaData = useMemo(
    () => ({
      FromDate: moment(fromDate).format('DD-MMM-YYYY'),
      ToDate: moment(toDate).format('DD-MMM-YYYY'),
      MessageType: messageType?.name || '',
      PolicyType: policyType?.name || '',
      PolicyCategory: policyCategory?.name || '',
      SentStatus: sentStatus?.name || '',
      BillingStatus: billingStatus?.name || '',
      dataType: 'entry',
    }),
    [billingStatus, fromDate, messageType, policyCategory, policyType, sentStatus, toDate]
  )
  const fetchData = () => {
    GetRequest(urls.whatsappPoliciesAndTemplates, { CompanyID: intCompanyID, WhatsappTypeID })
      .then((res) => {
        if (res.status !== true) {
          throw new Error(res.ErrMessage || 'Request Failed')
        }
        if (!Array.isArray(res.Data.Table1) || res.Data.Table1.length === 0) {
          throw new Error('Invalid response data')
        }
        const list = [...res.Data.Table1.map((item) => ({ id: item.SMSType, name: item.Header }))]
        setPolicyTypeList(list)
        setPolicyType(list[0])
      })
      .catch((err) => {
        setAlert({ show: true, msg: err.message || 'Request Failed', nextId: '' })
      })
      .finally(() => {
        setLoader(false)
      })
  }

  useEffect(() => {
    fetchData()
  }, [])

  const getPreviewPosition = (buttonRect) => {
    const cardWidth = 380
    const gutter = 12
    const viewportPadding = 16
    const spaceOnRight = window.innerWidth - buttonRect.right

    const left =
      spaceOnRight >= cardWidth + gutter ? buttonRect.right + gutter : Math.max(viewportPadding, buttonRect.left - cardWidth - gutter)

    const top = Math.min(Math.max(viewportPadding, buttonRect.top - 24), window.innerHeight - 220)

    return { top, left }
  }

  const handlePreviewOpen = (event, row) => {
    const buttonRect = event.currentTarget.getBoundingClientRect()
    setPreviewPosition(getPreviewPosition(buttonRect))
    setPreviewData({
      Header: row?.Header || '',
      MessageTemplate: row?.MessageTemplate || 'No Preview History',
    })
  }

  const handlePreviewClose = () => {
    setPreviewPosition(null)
  }

  const tableData = useMemo(
    () =>
      rawTableData.map((item, index) => ({
        ...item,
        SN: index + 1,
        dataType: 'entry',
        ISSent: !item.ISSent ? 'Failed' : 'Success',
        IsBilled: !item.IsBilled ? 'Not Billed' : 'Billed',
        ViewMsg_view: (
          <Button
            onClick={(event) => handlePreviewOpen(event, item)}
            sx={{ minWidth: 'auto', textTransform: 'capitalize', padding: '2px 8px' }}
          >
            View
          </Button>
        ),
      })),
    [rawTableData]
  )
  const handleGenerateReport = () => {
    const dateDifference = Math.abs(toDate.diff(fromDate, 'days'))

    if (dateDifference > 31) {
      setAlert({ show: true, msg: 'This report cannot be taken for more than 31 days', nextId: '' })
      return
    }

    setLoader(true)
    setRawTableData([])
    const requestPayload = {
      CompanyID: intCompanyID,
      FromDate: moment(fromDate).format('YYYY-MM-DD'),
      ToDate: moment(toDate).format('YYYY-MM-DD'),
      MessageType: messageType.id,
      PolicyType: policyType.id,
      PolicyCategory: policyCategory.id,
      isSent: sentStatus.id,
      isBilled: billingStatus.id || 0,
      WhatsappType: WhatsappTypeID || 0,
    }

    GetRequest(urls.whatsappDetailedTransactionsReport, requestPayload)
      .then((response) => {
        if (response.status !== true) {
          throw new Error(response?.ErrMessage || 'Request Failed')
        }
        if (!Array.isArray(response.Data.Table) || response.Data.Table.length === 0) {
          throw new Error('No Data Found')
        }
        const list = response.Data.Table.map((item) => ({
          ...item,
          MessageTemplate: replacePlaceholdersWithArrayValues(item.MessageTemplate, item.MessageTags.split('|~|')),
        }))

        setRawTableData(list)
      })
      .catch((error) => {
        setAlert({ show: true, msg: error.message || 'Request Failed', nextId: '' })
        return null
      })
      .finally(() => {
        setLoader(false)
      })
  }

  function replacePlaceholdersWithArrayValues(originalString, replacementArray) {
    let count = 0
    const placeholderRegex = /\[[^\]]+\]/g
    const newString = originalString.replace(placeholderRegex, (match, index) => {
      const replString = replacementArray[count]
      count += 1
      return replString
    })
    return newString
  }
  const params = {
    view: 'General Settings',
    tab: 521,
  }
  const goToTabs = () => {
    setSearchParams(params)
  }
  return (
    <Item sx={{ padding: '0' }} className="reports">
      <ReportHeading reportName={reportName.slice(0, -7)} />
      <Box sx={{ ...sx.reportFlex, justifyContent: 'start', paddingTop: '16px !important', gap: '12px' }}>
        <Item sx={{ ...sx.itemsx, minWidth: '230px' }} className="input--container bookingDatePicker">
          <Datepicker
            label="Sent From Date"
            name="whatsappMessageConsumptionFromDate"
            id="whatsappMessageConsumptionFromDate"
            value={fromDate}
            endDate={toDate}
            dateChange={setFromDate}
            width="100%"
          />
        </Item>
        <Item sx={{ ...sx.itemsx, minWidth: '230px' }} className="input--container bookingDatePicker">
          <Datepicker
            label="Sent To Date"
            name="whatsappMessageConsumptionToDate"
            id="whatsappMessageConsumptionToDate"
            value={toDate}
            startDate={fromDate}
            dateChange={setToDate}
            width="100%"
          />
        </Item>
        <Item sx={{ ...sx.itemsx, minWidth: '230px' }}>
          <DropDownOA
            id="whatsappCommunicationMessageType"
            label="Message Type"
            width="100%"
            data={messageTypeOptions}
            idKey="id"
            dataKey="name"
            value={messageType?.name || ''}
            defaultvalue={messageType?.name || ''}
            noCross={true}
            onSelect={(value) => setMessageType(value || messageTypeOptions[0])}
          />
        </Item>
        <Item sx={{ ...sx.itemsx, minWidth: '300px' }}>
          <DropDownOA
            id="whatsappCommunicationPolicyType"
            label="Policy Type"
            width="100%"
            data={policyTypeList}
            idKey="id"
            dataKey="name"
            value={policyType?.name || ''}
            defaultvalue={policyType?.name || ''}
            noCross={true}
            onSelect={(value) => setPolicyType(value)}
          />
        </Item>
        <Item sx={{ ...sx.itemsx, minWidth: '230px' }}>
          <DropDownOA
            id="whatsappCommunicationPolicyCategory"
            label="Policy Category"
            width="100%"
            data={policyCategoryOptions}
            idKey="id"
            dataKey="name"
            value={policyCategory?.name || ''}
            defaultvalue={policyCategory?.name || ''}
            noCross={true}
            onSelect={(value) => setPolicyCategory(value || policyCategoryOptions[0])}
          />
        </Item>
        <Item sx={{ ...sx.itemsx, minWidth: '230px' }}>
          <DropDownOA
            id="whatsappCommunicationSentStatus"
            label="Sent Status"
            width="100%"
            data={sentStatusOptions}
            idKey="id"
            dataKey="name"
            value={sentStatus?.name || ''}
            defaultvalue={sentStatus?.name || ''}
            noCross={true}
            onSelect={(value) => setSentStatus(value || sentStatusOptions[0])}
          />
        </Item>
        <Item sx={{ ...sx.itemsx, minWidth: '230px' }}>
          <DropDownOA
            id="whatsappCommunicationBillingStatus"
            label="Billing Status"
            width="100%"
            data={billingStatusOptions}
            idKey="id"
            dataKey="name"
            value={billingStatus?.name || ''}
            defaultvalue={billingStatus?.name || ''}
            noCross={true}
            onSelect={(value) => setBillingStatus(value || billingStatusOptions[0])}
          />
        </Item>
        <Item sx={sx.itemsx}>
          <Button
            variant="contained"
            id="whatsappCommunicationLoadButton"
            style={{ ...sx.fullHeight, ...sx.novp }}
            className="btn btn-primary"
            onClick={handleGenerateReport}
          >
            LOAD
          </Button>
        </Item>
        <Item sx={sx.itemsx}>
          <Button style={{ ...sx.fullHeight, ...sx.novp }} className="btn btn-primary" variant="outlined" onClick={() => goToTabs()}>
            Back to Policy
          </Button>
        </Item>
      </Box>
      {tableData.length ? (
        <TableComponent
          reportName={reportName}
          criteriaHeaders={criteriaHeaders}
          criteriaData={[criteriaData]}
          tableData={tableData}
          mainHeader={WhatsAppDetailedHeaders}
          isShowManageColumn={false}
        />
      ) : null}
      <Popover
        open={Boolean(previewPosition)}
        onClose={handlePreviewClose}
        anchorReference="anchorPosition"
        anchorPosition={previewPosition || { top: 0, left: 0 }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: 380,
            maxWidth: 'calc(100vw - 24px)',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 14px 32px rgba(0,0,0,0.22)',
            border: '1px solid rgba(0,0,0,0.06)',
          },
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: '#fff',
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: '18px', fontWeight: 700, lineHeight: 1.2 }}>{previewData.Header}</Typography>
          <IconButton
            size="small"
            onClick={handlePreviewClose}
            sx={{
              color: '#fff',
              padding: '2px',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' },
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>
        <Box
          sx={{
            px: 2,
            py: 2,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: '15px',
            fontWeight: 500,
            lineHeight: 1.55,
            color: '#333741',
            minHeight: 140,
            maxHeight: 320,
            overflowY: 'auto',
            backgroundColor: '#fff',
          }}
        >
          {previewData.MessageTemplate || 'No Preview History'}
        </Box>
      </Popover>
    </Item>
  )
}
